import { Alert, Checkbox, Flex, Input, Select, Typography } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { Progress, Modal, ModalHeader, ModalFooter, Button } from "reactstrap";
import { CSVLink } from "react-csv";
import {
  formatCellData,
  getColor,
  INPUT,
  CHECKBOX,
  NODATA,
  colors,
} from "./utils";
import ExcelJS from "exceljs/dist/exceljs";
import saveAs from "file-saver";
import { useTranslation } from "react-i18next";

const Index = ({
  open,
  setOpen,
  columns,
  dataSource,
  notExportCols = [],
  isUseDefaultSeq = false,
  fileName: defaultFileName = "",
}) => {
  const { t } = useTranslation(["configure"]);
  const [fileName, setFileName] = useState(defaultFileName);
  const [headers, setHeaders] = useState(null);
  const [errMessage, setErrMessage] = useState(null);
  const [headerSelected, setHeaderSelected] = useState([]);
  const [exporting, setExporting] = useState(false);
  const csvRef = useRef(null);
  const [data, setData] = useState(null);
  const optionsExt = [
    { label: ".xslx", value: "xlsx" },
    { label: ".csv", value: "csv" },
  ];
  const [exportType, setExportType] = useState("xslx");

  const reset = () => {
    setOpen(false);
    setErrMessage(null);
    setHeaders(null);
    setHeaderSelected([]);
  };

  // Xuất file định dạng .xslx
  const exportXLSX = async () => {
    if (!fileName || !fileName.trim()) {
      setErrMessage({ type: INPUT, detail: t("fileNameError") });
      return;
    }
    if (!headerSelected.length) {
      setErrMessage({ type: CHECKBOX, detail: t("columnsError") });
      return;
    }

    setExporting(true);
    setTimeout(() => {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Export");
      const headerExport = headers.filter((el) =>
        headerSelected.includes(el.value)
      );
      sheet.addRow([
        ...(isUseDefaultSeq ? [t("Seq")] : []),
        ...headerExport.map((el) => el.label),
      ]);
      dataSource.forEach((row, index) => {
        let arr = isUseDefaultSeq ? [index + 1] : [];
        headerExport.forEach((header) => {
          for (const property in row) {
            if (
              property &&
              typeof property === "string" &&
              header.value === property
            ) {
              arr.push(formatCellData(row[property]));
            }
          }
        });
        sheet.addRow(arr);
      });
      sheet.columns.forEach((column) => {
        const lengths = column.values.map((v) => v.toString().length);
        const maxLength = Math.max(
          ...lengths.filter((v) => typeof v === "number")
        );
        column.width = maxLength;
      });
      workbook.xlsx.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          `export-excel.xlsx`
        );
      });
      setOpen(false);
    }, 1000);
  };

  // Xuất file định dạng .csv
  const exportCSV = () => {
    if (!fileName || !fileName.trim()) {
      setErrMessage({ type: INPUT, detail: t("fileNameError") });
      return;
    }
    if (!headerSelected.length) {
      setErrMessage({ type: CHECKBOX, detail: t("columnsError") });
      return;
    }

    const headerExport = headers.filter((el) =>
      headerSelected.includes(el.value)
    );
    const headerExportObj = headerExport.reduce(
      (a, v) => ({ ...a, [v.value]: v.label }),
      {}
    );
    const exportData = [];
    dataSource.forEach((row, index) => {
      let obj = isUseDefaultSeq ? { [t("Seq")]: index + 1 } : {};
      for (const property in row) {
        if (
          property &&
          typeof property === "string" &&
          headerExportObj[property]
        ) {
          obj = Object.assign(obj, {
            [headerExportObj[property]]: formatCellData(row[property]),
          });
        }
      }
      exportData.push(obj);
    });
    setData(exportData);

    setTimeout(() => {
      try {
        csvRef.current.link.click();
      } catch (err) {
      } finally {
        reset();
        setExporting(false);
      }
    }, 1500);
  };

  useEffect(() => {
    if (!dataSource || !dataSource.length) {
      setErrMessage({ type: NODATA, detail: t("noDataToExport") });
      return;
    }

    const defaultHeaderSelected = [];

    const headers = columns.reduce((a, v) => {
      if (
        typeof v.accessor === "string" &&
        !notExportCols.includes(v.Header) &&
        typeof v.Header == "string"
      ) {
        // Select All Columns by Default
        defaultHeaderSelected.push(v.accessor);
        return [...a, { label: v.Header, value: v.accessor }];
      }
      return a;
    }, []);

    setHeaders(headers);
    setHeaderSelected(defaultHeaderSelected);
  }, []);

  const renderForm = () => (
    <>
      {errMessage && (
        <div className="my-2">
          <Alert
            message={errMessage.detail}
            type="error"
            showIcon
            // Disallow clear message types in Array
            closable={![NODATA].includes(errMessage.type)}
            onClose={() => setErrMessage(null)}
          />
        </div>
      )}

      {headers && headers.length ? (
        <>
          <div className="my-3">
            <Typography.Text
              style={{
                fontSize: "1rem",
                paddingLeft: "10px",
                color: getColor(errMessage, INPUT),
                borderLeft: `4px solid ${getColor(errMessage, INPUT)}`,
              }}
            >
              {t("fileName")}
            </Typography.Text>
            <div className="mt-1">
              <Flex align="center">
                <Input
                  value={fileName}
                  onChange={(e) => {
                    if (errMessage) {
                      setErrMessage(null);
                    }
                    setFileName(e.target.value);
                  }}
                  style={{ flex: 1 }}
                />
                <Select
                  options={optionsExt}
                  value={exportType}
                  onChange={(val) => setExportType(val)}
                  className="ml-1"
                />
              </Flex>
            </div>
          </div>

          <Typography.Text
            style={{
              fontSize: "1rem",
              paddingLeft: "10px",
              color: getColor(errMessage, CHECKBOX),
              borderLeft: `4px solid ${getColor(errMessage, CHECKBOX)}`,
            }}
          >
            {t("columnsToExport")}
          </Typography.Text>
          <div className="mt-2">
            <Checkbox.Group
              options={headers}
              defaultValue={headerSelected}
              onChange={(val) => {
                if (errMessage) {
                  setErrMessage(null);
                }
                setHeaderSelected(val);
              }}
            />
          </div>
        </>
      ) : null}
    </>
  );

  const renderExporting = () => (
    <>
      <Typography.Text style={{ color: colors.success }} className="mt-4">
        Đang xuất file excel. Vui lòng chờ.
      </Typography.Text>
      <div className="mt-2">
        <Progress animated value="100" barClassName="reactstrap-progressbar" />
      </div>
    </>
  );

  return (
    <>
      <Modal isOpen={open} toggle={reset}>
        <ModalHeader>{t("EXPORT CSV")}</ModalHeader>
        <div className="px-4 mb-4">
          {exporting ? renderExporting() : renderForm()}
        </div>

        {data && (
          <CSVLink
            data={data}
            filename={`${fileName}.csv`}
            className="hidden"
            ref={csvRef}
            target="_blank"
          ></CSVLink>
        )}

        {!exporting ? (
          <ModalFooter>
            <Button color="danger" className="ml-2" onClick={reset}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="18"
                width="18"
                viewBox="0 0 512 512"
                className="mr-1"
                style={{ marginBottom: 3 }}
              >
                <path
                  fill="white"
                  d="M377.9 105.9L500.7 228.7c7.2 7.2 11.3 17.1 11.3 27.3s-4.1 20.1-11.3 27.3L377.9 406.1c-6.4 6.4-15 9.9-24 9.9c-18.7 0-33.9-15.2-33.9-33.9l0-62.1-128 0c-17.7 0-32-14.3-32-32l0-64c0-17.7 14.3-32 32-32l128 0 0-62.1c0-18.7 15.2-33.9 33.9-33.9c9 0 17.6 3.6 24 9.9zM160 96L96 96c-17.7 0-32 14.3-32 32l0 256c0 17.7 14.3 32 32 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-64 0c-53 0-96-43-96-96L0 128C0 75 43 32 96 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32z"
                />
              </svg>
              {t("Close")}
            </Button>
            {errMessage && [NODATA].includes(errMessage.type) ? null : (
              <Button
                color="success"
                className="ml-2"
                type="submit"
                onClick={() => {
                  if (exportType === "xslx") {
                    exportXLSX();
                  } else {
                    exportCSV();
                  }
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="16"
                  width="16"
                  viewBox="0 0 512 512"
                  className="mr-1"
                  style={{ marginBottom: 3 }}
                >
                  <path
                    fill="white"
                    d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L369 209z"
                  />
                </svg>
                {t("Apply")}
              </Button>
            )}
          </ModalFooter>
        ) : null}
      </Modal>
    </>
  );
};

export default Index;
